import { apiRequest } from '../../shared/api';

export interface OrganizationSettings {
  organization_id: string;
  timezone: string;
  currency: string;
  language_code: string;
  date_format: string;
  number_format: string;
  fiscal_year_start_month: number;
  business_hours?: {
    start: string;
    end: string;
    timezone?: string;
  };
  working_days?: string[];
  default_policies?: {
    document_review_period_days?: number;
    task_reminder_days?: number;
    escalation_threshold_hours?: number;
  };
  updated_at?: string;
}

export interface Department {
  id: string;
  organization_id: string;
  department_code: string;
  name: string;
  description?: string;
  department_type?: string;
  status: string;
  created_at?: string;
}

export interface OrganizationData {
  id: string;
  organization_code: string;
  legal_name: string;
  trading_name?: string;
  status: string;
  default_timezone?: string;
  default_currency?: string;
  default_language?: string;
}

export const administrationService = {
  async getSettings(): Promise<{ organization: OrganizationData; settings: OrganizationSettings }> {
    const res = await apiRequest<{ organization: OrganizationData; settings: OrganizationSettings }>('/administration/settings');
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async updateSettings(settings: Partial<OrganizationSettings>): Promise<OrganizationSettings> {
    const res = await apiRequest<OrganizationSettings>('/administration/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async getDepartments(): Promise<Department[]> {
    const res = await apiRequest<Department[]>('/administration/departments');
    const unwrapped = (res as any)?.data !== undefined ? (res as any).data : res;
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async createDepartment(data: {
    department_code?: string;
    name: string;
    description?: string;
    department_type?: string;
  }): Promise<Department> {
    const res = await apiRequest<Department>('/administration/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async deactivateDepartment(id: string): Promise<void> {
    await apiRequest(`/administration/departments/${id}`, {
      method: 'DELETE',
    });
  },

  async getEmailTemplates(): Promise<{
    config: {
      enabled: boolean;
      senderName: string;
      fromAddress: string;
      replyTo: string;
      portalBaseUrl: string;
      organizationName: string;
      resendApiKeyConfigured: boolean;
      provider: string;
    };
    governance?: EmailGovernanceSettings;
    rolePolicies?: EmailRolePolicy[];
    totalTemplates: number;
    templates: EmailTemplateItem[];
  }> {
    const res = await apiRequest<any>('/administration/email-notifications/templates');
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async getEmailGovernance(): Promise<EmailGovernanceSettings> {
    const res = await apiRequest<any>('/administration/email-notifications/governance');
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async updateEmailGovernance(payload: Partial<EmailGovernanceSettings> & {
    resendApiKeyPlaintext?: string;
    clearResendApiKey?: boolean;
  }): Promise<EmailGovernanceSettings> {
    const res = await apiRequest<any>('/administration/email-notifications/governance', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async getEmailRolePolicies(): Promise<EmailRolePolicy[]> {
    const res = await apiRequest<any>('/administration/email-notifications/role-policies');
    const unwrapped = (res as any)?.data !== undefined ? (res as any).data : res;
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async updateEmailRolePolicy(roleCode: string, payload: Partial<EmailRolePolicy>): Promise<EmailRolePolicy> {
    const res = await apiRequest<any>(`/administration/email-notifications/role-policies/${encodeURIComponent(roleCode)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async updateEmailTemplate(templateKey: string, payload: Partial<EmailTemplateItem>): Promise<EmailTemplateItem> {
    const res = await apiRequest<any>(`/administration/email-notifications/templates/${encodeURIComponent(templateKey)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async resetEmailTemplate(templateKey: string): Promise<EmailTemplateItem> {
    const res = await apiRequest<any>(`/administration/email-notifications/templates/${encodeURIComponent(templateKey)}/reset`, {
      method: 'POST',
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async getEmailOutbox(limit: number = 50): Promise<EmailOutboxItem[]> {
    const res = await apiRequest<any>(`/administration/email-notifications/outbox?limit=${limit}`);
    const unwrapped = (res as any)?.data !== undefined ? (res as any).data : res;
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async dispatchEmailNotification(payload: {
    templateKey: string;
    recipientEmail?: string;
    recipientRoles?: string[];
    confidentialityLevel?: string;
    identityMode?: string;
    variables?: Record<string, any>;
  }): Promise<any> {
    return apiRequest('/administration/email-notifications/dispatch', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async runEmailSlaSweep(): Promise<any> {
    const res = await apiRequest<any>('/administration/email-notifications/sla-sweep', {
      method: 'POST',
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async getFormTemplates(): Promise<any[]> {
    const res = await apiRequest<any>('/administration/forms');
    const unwrapped = (res as any)?.data !== undefined ? (res as any).data : res;
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async updateQuestion(
    id: string,
    payload: {
      label?: string;
      question_type?: string;
      options_json?: string;
      options?: string[];
      is_required?: boolean;
      help_text?: string;
      section_name?: string;
    }
  ): Promise<any> {
    const res = await apiRequest<any>(`/administration/forms/questions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async testResendConnection(params?: {
    apiKey?: string;
    fromAddress?: string;
    recipientEmail?: string;
  }): Promise<{
    success: boolean;
    apiKeyValid: boolean;
    apiKeySource: string;
    apiKeyMasked: string;
    isMockKey: boolean;
    senderDomain: string;
    domainVerified: boolean;
    domainStatus: string;
    accountDomains: Array<{ id: string; name: string; status: string }>;
    recommendation: string;
    error?: string;
    testEmailSent?: boolean;
    resendMessageId?: string;
    usedSender?: string;
    recipient?: string;
    sandboxFallbackUsed?: boolean;
  }> {
    const res = await apiRequest<any>('/administration/email-notifications/test-connection', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },

  async retryOutboxItem(outboxId: string): Promise<EmailOutboxItem> {
    const res = await apiRequest<any>(`/administration/email-notifications/outbox/${encodeURIComponent(outboxId)}/retry`, {
      method: 'POST',
    });
    return (res as any)?.data !== undefined ? (res as any).data : res;
  },
};

export interface EmailGovernanceSettings {
  organizationId: string;
  enabled: boolean;
  provider: string;
  deliveryMode: 'AUTO' | 'LIVE_RESEND' | 'SIMULATED_OUTBOX' | 'CRITICAL_ONLY';
  senderName: string;
  fromAddress: string;
  replyToAddress: string;
  portalBaseUrl: string;
  resendApiKeyConfigured: boolean;
  resendApiKeyMasked: string;
  isMockKey?: boolean;
  decryptionStatus?: string;
  resendWebhookSecretConfigured: boolean;
  complianceBccEmails: string[];
  emergencyEscalationEmails: string[];
  maxRetries: number;
  retryBackoffSeconds: number;
  dailyRateLimit: number;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  bypassQuietHoursForCritical: boolean;
  enforceJustCulturePiiQuarantine: boolean;
  redactConfidentialNarratives: boolean;
  blockAnonymousDirectOutreach: boolean;
  blockInternalSupportNotes: boolean;
  minimumRedactionLevel: 'NORMAL' | 'RESTRICTED' | 'CONFIDENTIAL';
  customFooterDisclaimer: string;
  morEscalationWarningHours: number;
  actionOverdueReminderDays: number;
  riskReviewReminderDays: number;
  executiveDigestEnabled: boolean;
  executiveDigestCron: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface EmailRolePolicy {
  organizationId: string;
  roleCode: string;
  roleDisplayName: string;
  emailEnabled: boolean;
  deliveryPreference: 'IMMEDIATE' | 'CRITICAL_ONLY' | 'DAILY_DIGEST' | 'DISABLED';
  maxConfidentialityLevel: 'NORMAL' | 'RESTRICTED' | 'CONFIDENTIAL';
  allowUnredactedNarrative: boolean;
  fallbackDistributionEmails: string[];
  autoCcEmails: string[];
  subscribedTemplateKeys: string[];
  ccTemplateKeys: string[];
  mutedTemplateKeys: string[];
  updatedBy?: string;
  updatedAt?: string;
}

export interface EmailTemplateItem {
  templateKey: string;
  pillarName: string;
  moduleDomain: string;
  regulationRef: string;
  eventTrigger: string;
  recipientRoles: string[];
  accentColor: string;
  badgeLabel: string;
  isSafetyCritical: boolean;
  isActive?: boolean;
  defaultConfidentiality: string;
  subjectTemplate: string;
  headlineTemplate?: string;
  introTemplate?: string;
  bodySectionTitle?: string;
  actionButtonLabel?: string;
  portalTab?: string;
  customHtmlOverride?: string;
  customToEmails?: string[];
  customCcEmails?: string[];
  customBccEmails?: string[];
  version?: number;
  renderedNormal: {
    subject: string;
    html: string;
    text: string;
    piiRedacted: boolean;
  };
  renderedConfidential: {
    subject: string;
    html: string;
    text: string;
    piiRedacted: boolean;
  };
}

export interface EmailOutboxItem {
  id: string;
  templateKey: string;
  moduleDomain: string;
  eventType: string;
  recipientEmail: string;
  recipientRole?: string;
  subject: string;
  confidentialityLevel: string;
  piiRedacted: boolean;
  isSafetyCritical: boolean;
  status: string;
  provider: string;
  providerMessageId?: string;
  errorMessage?: string;
  errorCategory?: string;
  retryCount?: number;
  createdAt: string;
  sentAt?: string;
}

export { adminFunctionsService } from './adminFunctionsService';

